import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Center,
  Stack,
  Title,
  Text,
  Button,
  Card,
  SimpleGrid,
  Checkbox,
  Group,
  Alert,
  Loader,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconLogout } from "@tabler/icons-react";
import { useTaskStore, type Task } from "../store/task.store";
import { useAuthStore } from "../store";
import { useCurrentUser, useLogout } from "../hooks/useAuth";
import { getDefaultRoute } from "../data/taskNavigation";
import { userPreferencesService } from "../services/userPreferences.service";

function buildDefaultSelection(
  permittedTasks: Task[],
  roleTaskIds: string[]
): Set<string> {
  const roleIdSet = new Set(roleTaskIds);
  return new Set(
    permittedTasks.filter((t) => roleIdSet.has(t.id)).map((t) => t.id)
  );
}

export default function TaskSelection() {
  const navigate = useNavigate();
  const { permittedTasks, roleTaskIds, selectedTasks, setSelectedTasks } =
    useTaskStore();
  const profile = useAuthStore((state) => state.profile);
  const { isLoading: isLoadingUser } = useCurrentUser();
  const logout = useLogout();
  const [selected, setSelected] = useState<Set<string>>(() =>
    buildDefaultSelection(permittedTasks, roleTaskIds)
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (selectedTasks.length > 0) {
      navigate(getDefaultRoute(selectedTasks.map((t) => t.slug)), {
        replace: true,
      });
    }
  }, [selectedTasks, navigate]);

  useEffect(() => {
    setSelected((prev) => {
      if (prev.size > 0) {
        return prev;
      }
      return buildDefaultSelection(permittedTasks, roleTaskIds);
    });
  }, [permittedTasks, roleTaskIds]);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const handlePendingWork = useCallback(() => {
    navigate("/pending-work");
  }, [navigate]);

  useEffect(() => {
    if (permittedTasks.length === 1 && !isLoadingUser) {
      const task = permittedTasks[0];
      setSelectedTasks([task]);
      void userPreferencesService.saveSelectedTasks([task.id]).catch(() => {
        // best-effort; do not block navigation
      });
      navigate(getDefaultRoute([task.slug]), { replace: true });
    }
  }, [permittedTasks, isLoadingUser, setSelectedTasks, navigate]);

  const toggleTask = useCallback((task: Task) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(task.id)) {
        next.delete(task.id);
      } else {
        next.add(task.id);
      }
      return next;
    });
  }, []);

  const handleContinue = useCallback(async () => {
    const tasks = permittedTasks.filter((t) => selected.has(t.id));
    setSelectedTasks(tasks);
    setIsSaving(true);
    try {
      await userPreferencesService.saveSelectedTasks(tasks.map((t) => t.id));
    } catch (error) {
      notifications.show({
        title: "Could not save preference",
        message: "Your selection is active for this session.",
        color: "orange",
      });
    } finally {
      setIsSaving(false);
    }
    navigate(getDefaultRoute(tasks.map((t) => t.slug)));
  }, [permittedTasks, selected, setSelectedTasks, navigate]);

  if (isLoadingUser) {
    return (
      <Center style={{ minHeight: "100vh" }}>
        <Loader size="lg" color="gcpBlue" />
      </Center>
    );
  }

  if (permittedTasks.length === 0) {
    return (
      <Center
        style={{
          minHeight: "100vh",
          backgroundColor: "var(--mantine-color-gray-0)",
        }}
      >
        <Stack align="center" gap="lg" maw={500} px="md">
          <Alert
            icon={<IconAlertCircle size={18} />}
            title="No Tasks Available"
            color="orange"
            w="100%"
          >
            <Stack gap="sm">
              <Text size="sm">
                No tasks are currently linked to your account.
              </Text>
              <Text size="sm">
                Try logging out and logging back in. If the issue persists,
                please contact an administrator.
              </Text>
            </Stack>
          </Alert>
          <Text size="sm" c="dimmed" ta="center">
            You can view Pending List without selecting a clinical task.
          </Text>
          <Button onClick={handlePendingWork} fullWidth>
            Open Pending List
          </Button>
          <Button
            variant="outline"
            color="gray"
            leftSection={<IconLogout size={16} />}
            onClick={handleLogout}
            fullWidth
          >
            Log Out
          </Button>
        </Stack>
      </Center>
    );
  }

  return (
    <Center
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--mantine-color-gray-0)",
      }}
    >
      <Stack align="center" gap="xl" maw={700} w="100%" px="md">
        <Stack align="center" gap="xs">
          <Title order={2} c="gcpBlue">
            What are you working on today?
          </Title>
          <Text c="dimmed" size="sm">
            Welcome back, {profile?.fullName}. Select your tasks to get started.
          </Text>
        </Stack>

        <SimpleGrid cols={{ base: 1, sm: 2 }} w="100%" spacing="md">
          {permittedTasks.map((task) => {
            const isSelected = selected.has(task.id);
            return (
              <Card
                key={task.id}
                withBorder
                padding="md"
                radius="md"
                style={{
                  cursor: "pointer",
                  borderColor: isSelected
                    ? "var(--mantine-color-gcpBlue-6)"
                    : undefined,
                  backgroundColor: isSelected
                    ? "var(--mantine-color-gcpBlue-0)"
                    : undefined,
                }}
                onClick={() => toggleTask(task)}
              >
                <Group>
                  <Checkbox
                    checked={isSelected}
                    onChange={() => toggleTask(task)}
                    onClick={(e) => e.stopPropagation()}
                    color="gcpBlue"
                  />
                  <Text fw={500}>{task.name}</Text>
                </Group>
              </Card>
            );
          })}
        </SimpleGrid>

        <Button
          size="md"
          color="gcpBlue"
          disabled={selected.size === 0 || isSaving}
          loading={isSaving}
          onClick={handleContinue}
          w={{ base: "100%", sm: "auto" }}
          px="xl"
        >
          Continue
        </Button>
        <Stack align="center" gap="xs" w="100%">
          <Text size="sm" c="dimmed" ta="center">
            You can view Pending List without selecting a clinical task.
          </Text>
          <Button variant="light" onClick={handlePendingWork}>
            Open Pending List
          </Button>
        </Stack>
      </Stack>
    </Center>
  );
}
